import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { Add, Biotech, Room } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { roomsAPI } from '../../../rooms/services/rooms';
import { equipmentAPI } from '../../../equipment/services/equipment';
import { useAuth } from '../../../../context/AuthContext';
import LoadErrorAlert from '../LoadErrorAlert';

// Where equipment creation returns to when done (step index is 0-based)
const RETURN_TO = '/onboarding?step=3';

export default function Step4Equipment() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [equipmentByRoom, setEquipmentByRoom] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user?.department?.department_id) return;
    setLoading(true);
    setLoadError(false);
    try {
      const [roomsRes, equipRes] = await Promise.all([
        roomsAPI.listRoomsByDepartment(user.department.department_id),
        equipmentAPI.listEquipmentByDepartment(),
      ]);
      const roomList = roomsRes.data?.rooms || [];
      const equipList = equipRes.data?.equipment || [];
      setRooms(roomList);
      const byRoom = {};
      roomList.forEach(r => { byRoom[r.room_id] = []; });
      equipList.forEach(e => {
        if (e.room_id && byRoom[e.room_id] !== undefined) {
          byRoom[e.room_id].push(e);
        }
      });
      setEquipmentByRoom(byRoom);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddEquipment = (roomId) => {
    // The template page reads roomId from the path, not the query string
    navigate(`/rooms/${roomId}/equipment/select-template`, { state: { returnTo: RETURN_TO } });
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t('onboarding:step4.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('onboarding:step4.subtitle')}
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : loadError ? (
        <LoadErrorAlert onRetry={fetchData} />
      ) : rooms.length === 0 ? (
        <Alert severity="warning">
          {t('onboarding:step4.no_rooms')}
        </Alert>
      ) : (
        <Stack spacing={2}>
          {rooms.map(room => {
            const equipment = equipmentByRoom[room.room_id] || [];
            return (
              <Card key={room.room_id} variant="outlined">
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Room color="action" fontSize="small" />
                      <Typography fontWeight={700}>{room.name}</Typography>
                      <Chip
                        label={t('onboarding:step4.equipment_count', { count: equipment.length })}
                        size="small"
                        color={equipment.length > 0 ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Stack>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => handleAddEquipment(room.room_id)}
                    >
                      {t('onboarding:step4.add_equipment')}
                    </Button>
                  </Stack>

                  {equipment.length > 0 && (
                    <>
                      <Divider sx={{ mb: 1 }} />
                      <Stack spacing={0.5}>
                        {equipment.map(e => (
                          <Stack
                            key={e.equipment_id}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Biotech color="action" sx={{ fontSize: 16 }} />
                            <Typography variant="body2">{e.name}</Typography>
                            {e.manufacturer && (
                              <Chip
                                label={e.manufacturer}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: 10, height: 20 }}
                              />
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
